import { useCallback, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  fetchAllFeeProfiles,
  removeVatProfile,
} from "../../app-main/actions/fee-profile";
import { selectAuthAccessToken } from "../../app-main/selectors/auth.selectors";
import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";

/** Deactivates a tax registration and refreshes the guest's fee profiles. */
const useDeleteTaxRegistration = () => {
  const dispatch = useDispatch();
  const accessToken = useSelector(selectAuthAccessToken);
  const ssoId = useSelector(selectProfileSSOid);

  const [isDeleting, setIsDeleting] = useState(false);
  const [hasDeleteFailed, setHasDeleteFailed] = useState(false);

  const resetDeleteError = useCallback(() => {
    setHasDeleteFailed(false);
  }, []);

  /** Resolves `true` once the registration is gone and the store is back in sync. */
  const deleteRegistration = useCallback(
    async (registrationId: string) => {
      if (!accessToken || !ssoId) {
        setHasDeleteFailed(true);
        return false;
      }

      setIsDeleting(true);
      setHasDeleteFailed(false);

      try {
        await dispatch(removeVatProfile(accessToken, ssoId, registrationId));
        // The remove leaves the list as it was, so read it back.
        await dispatch(fetchAllFeeProfiles(accessToken, ssoId));
        return true;
      } catch {
        setHasDeleteFailed(true);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [accessToken, dispatch, ssoId],
  );

  return { deleteRegistration, hasDeleteFailed, isDeleting, resetDeleteError };
};

export { useDeleteTaxRegistration };
